import { useQuery } from '@tanstack/react-query'
import { readServiceHealth } from '../lib/gateway.ts'

/**
 * Which estate this tab is pointed at, in a word.
 *
 * docs/ux/PLATFORM-ADMIN.md §5.1 and docs/DECISIONS.md, "The environment label comes from
 * the API, not from the build". Pre-prod and prod are the same application against
 * different data and the actions here are irreversible, so revoking a key on prod because
 * the tab was the pre-prod one is the exact mistake this label exists to prevent.
 *
 * **When the read fails the label says so and never guesses.** Not blank, not the build's
 * own belief, and above all not a fall back to pre-prod — the reading that would make an
 * unlabelled prod tab look safe. It is a label, not a gate: the application keeps working
 * and the label corrects itself when a later read lands.
 */

/** What the masthead and the page title show while nothing has answered. */
export const UNKNOWN_ENVIRONMENT_LABEL = 'environment unknown'

/**
 * Every deployment this console can be pointed at, by the name its API reports.
 *
 * The raw name and the word shown for it are **one table**, read from both ends: the
 * masthead shows the label, and `tenants/slug.ts` picks a tenant's hostname zone from the
 * name. Written twice they would drift, and a console that said "prod" over a pre-prod
 * hostname would be worse than one that said neither.
 */
export const DEPLOYMENTS = ['local', 'preprod', 'prod'] as const

/** One of the three names above — never a string the API made up. */
export type Deployment = (typeof DEPLOYMENTS)[number]

/** The word a human is shown for each deployment. */
export const DEPLOYMENT_LABELS: Readonly<Record<Deployment, string>> = {
  local: 'local',
  preprod: 'pre-prod',
  prod: 'prod',
}

/**
 * The API's `environment` value as a deployment, or `undefined` when it is not one.
 *
 * `undefined` covers the read that has not landed, the read that failed, and the API's own
 * `unknown` (a Worker running without its `ENVIRONMENT` var). All three mean the same
 * thing to every caller — *this console does not know which estate it is showing* — and
 * none of them may be resolved by guessing.
 */
export function deploymentOf(environment: string | undefined): Deployment | undefined {
  return DEPLOYMENTS.find((deployment) => deployment === environment)
}

/** The API's `environment` value, as a word for a human. */
export function environmentLabel(environment: string | undefined): string {
  const deployment = deploymentOf(environment)
  return deployment === undefined ? UNKNOWN_ENVIRONMENT_LABEL : DEPLOYMENT_LABELS[deployment]
}

export const SERVICE_HEALTH_QUERY_KEY = ['serviceHealth'] as const

/** How often a failed read is tried again, in the background. */
const RETRY_INTERVAL_MS = 30_000

export type Environment = {
  /** The word to show. Never empty. */
  readonly label: string
  /** False while the label is a statement of ignorance rather than of fact. */
  readonly known: boolean
  /**
   * Which estate this is, for a screen that has to *do* something with the answer rather
   * than print it. `undefined` whenever `known` is false — there is no fallback.
   */
  readonly deployment: Deployment | undefined
}

/**
 * The `/health` read, as one query that every caller shares.
 *
 * The masthead reads it for the environment word (`useEnvironment`) and Your account reads it for
 * the API's own build (`build`); both observe this one key, so a second caller is a cache hit and
 * never a second request.
 */
export function useServiceHealth() {
  return useQuery({
    queryKey: SERVICE_HEALTH_QUERY_KEY,
    queryFn: readServiceHealth,
    // The environment of a deployment does not change while a tab is open; only a
    // failure is worth asking about again.
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: (query) => (query.state.status === 'error' ? RETRY_INTERVAL_MS : false),
  })
}

export function useEnvironment(): Environment {
  const health = useServiceHealth()

  const deployment = deploymentOf(health.data?.environment)
  return {
    label: deployment === undefined ? UNKNOWN_ENVIRONMENT_LABEL : DEPLOYMENT_LABELS[deployment],
    known: deployment !== undefined,
    deployment,
  }
}
